import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Button,
  Badge,
  Divider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  useToast,
  Spinner,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Progress,
  Link,
} from "@chakra-ui/react";
import { FiCalendar, FiDollarSign, FiAlertTriangle, FiCheckCircle, FiXCircle, FiDownload, FiMail } from "react-icons/fi";
import { 
  useGetUserSubscription, 
  useCancelSubscription, 
  useReactivateSubscription, 
  useUpdateSubscriptionPlan,
  useGetPlans 
} from "../../services/settingsService";

const SubscriptionSection = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isReactivateDialogOpen, setIsReactivateDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const toast = useToast();

  // Fetch subscription and plans data
  const { subscriptionData, subscriptionIsLoading, refetchSubscription } = useGetUserSubscription();
  const { plansData, plansIsLoading } = useGetPlans();
  const { cancelSubscriptionPayload, cancelSubscriptionIsLoading } = useCancelSubscription(() => {
    refetchSubscription();
    setIsCancelDialogOpen(false);
    setCancelReason("");
  });
  const { reactivateSubscriptionPayload, reactivateSubscriptionIsLoading } = useReactivateSubscription(() => {
    refetchSubscription();
    setIsReactivateDialogOpen(false);
  });
  const { updatePlanPayload, updatePlanIsLoading } = useUpdateSubscriptionPlan(() => {
    refetchSubscription();
    onClose();
    setSelectedPlan("");
  });

  const handleUpdatePlan = () => {
    if (!selectedPlan) {
      toast({
        title: "Error",
        description: "Please select a plan",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    updatePlanPayload({ new_plan: selectedPlan });
  };

  const handleCancelSubscription = () => {
    if (!cancelReason.trim()) {
      toast({
        title: "Error",
        description: "Please provide a reason for cancellation",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    cancelSubscriptionPayload({ reason: cancelReason });
  };

  const handleReactivateSubscription = () => {
    reactivateSubscriptionPayload();
  };

  // Quick Actions Handlers
  const handleDownloadInvoice = () => {
    // Try to get invoice from subscription data first
    if (subscriptionData?.latest_invoice?.hosted_invoice_url) {
      window.open(subscriptionData.latest_invoice.hosted_invoice_url, '_blank');
      return;
    }
    
    // Fallback: check if we have any payment with invoice
    if (subscriptionData?.recent_payments?.length > 0) {
      const paymentWithInvoice = subscriptionData.recent_payments.find(p => p.invoice_pdf);
      if (paymentWithInvoice?.invoice_pdf) {
        window.open(paymentWithInvoice.invoice_pdf, '_blank');
        return;
      }
    }
    
    // If no invoice available, show message
    toast({
      title: "No Invoice Available",
      description: "No invoice is available for download at this time. Please contact support if you need an invoice.",
      status: "info",
      duration: 4000,
      isClosable: true,
      position: "top",
    });
  };

  const handleViewBillingHistory = () => {
    // Navigate to the billing section
    const billingSection = document.getElementById('billing-section');
    if (billingSection) {
      billingSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Fallback: scroll to top of the page and show billing tab
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast({
        title: "Billing History",
        description: "Please switch to the Billing tab to view payment history.",
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleContactSupport = () => {
    // Open email client with support email
    const subject = encodeURIComponent("Billing Support Request");
    const body = encodeURIComponent(`Hello Support Team,\n\nI need assistance with my billing/subscription.\n\nSubscription Details:\n- Plan: ${subscriptionData?.plan || 'N/A'}\n- Status: ${subscriptionData?.status || 'N/A'}\n- Customer ID: ${subscriptionData?.subscription_id || 'N/A'}\n- Company: ${subscriptionData?.company || 'N/A'}\n\nPlease provide details about your issue:\n\n\n\nThank you.\n\nBest regards,\n${subscriptionData?.user?.name || 'User'}`);
    const mailtoLink = `mailto:support@weetrak.org?subject=${subject}&body=${body}`;
    
    // Try to open email client, fallback to showing email address
    try {
      window.open(mailtoLink, '_blank');
    } catch (error) {
      toast({
        title: "Contact Support",
        description: "Email: support@weetrak.org",
        status: "info",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const formatCurrency = (amount, currency = 'usd') => {
    // Handle null, undefined, or invalid amounts
    if (!amount) {
      return 'N/A';
    }
    
    // If amount is already a formatted string (like "$49.00"), return it as is
    if (typeof amount === 'string' && amount.includes('$')) {
      return amount;
    }
    
    // Convert to number if it's a string
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    // Check if the amount is already in dollars (not cents)
    // If amount is less than 1000, assume it's already in dollars
    const finalAmount = numericAmount < 1000 ? numericAmount : numericAmount / 100;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(finalAmount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'canceled':
        return 'red';
      case 'past_due':
        return 'orange';
      default:
        return 'gray';
    }
  };

  if (subscriptionIsLoading || plansIsLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color="gray.600">Loading subscription information...</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          <Heading size="md" color="gray.900" fontWeight="600">
            Subscription Management
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Manage your subscription plan and billing preferences
          </Text>
        </VStack>
        <Button
          colorScheme="blue"
          variant="outline"
          onClick={onOpen}
        >
          Change Plan
        </Button>
      </HStack>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Current Subscription */}
        <Card>
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Heading size="md" color="gray.900">
                Current Subscription
              </Heading>

              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Status</Text>
                  <Badge colorScheme={getStatusColor(subscriptionData?.status)} variant="subtle">
                    {subscriptionData?.status || 'No Subscription'}
                  </Badge>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Plan</Text>
                  <Text fontWeight="500">{subscriptionData?.plan || 'N/A'}</Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Amount</Text>
                  <Text fontWeight="500">
                    {subscriptionData?.amount_cents 
                      ? formatCurrency(subscriptionData.amount_cents, subscriptionData.currency)
                      : subscriptionData?.amount 
                        ? formatCurrency(subscriptionData.amount, subscriptionData.currency)
                        : 'N/A'
                    }
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Billing Cycle</Text>
                  <Text fontWeight="500">
                    {subscriptionData?.current_period_start && subscriptionData?.current_period_end
                      ? `${formatDate(subscriptionData.current_period_start)} - ${formatDate(subscriptionData.current_period_end)}`
                      : 'N/A'
                    }
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Next Payment</Text>
                  <Text fontWeight="500">
                    {subscriptionData?.next_payment 
                      ? formatDate(subscriptionData.next_payment)
                      : 'N/A'
                    }
                  </Text>
                </HStack>

                {subscriptionData?.cancel_at_period_end && (
                  <Box p={3} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                    <HStack spacing={2}>
                      <FiAlertTriangle color="orange.500" />
                      <Text fontSize="sm" color="orange.700">
                        Subscription will be canceled at the end of the current billing period
                      </Text>
                    </HStack>
                  </Box>
                )}
              </VStack>

              <Divider />

              <VStack spacing={3} align="stretch">
                {subscriptionData?.status === 'active' && !subscriptionData?.cancel_at_period_end ? (
                  <Button
                    colorScheme="red"
                    variant="outline"
                    onClick={() => setIsCancelDialogOpen(true)}
                  >
                    Cancel Subscription
                  </Button>
                ) : subscriptionData?.cancel_at_period_end ? (
                  <Button
                    colorScheme="green"
                    onClick={() => setIsReactivateDialogOpen(true)}
                    isLoading={reactivateSubscriptionIsLoading}
                  >
                    Reactivate Subscription
                  </Button>
                ) : (
                  <Button
                    colorScheme="blue"
                    onClick={onOpen}
                  >
                    Subscribe to a Plan
                  </Button>
                )}
              </VStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Subscription Details */}
        <Card>
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Heading size="md" color="gray.900">
                Subscription Details
              </Heading>

              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="500" color="gray.700" mb={1}>
                    Subscription ID
                  </Text>
                  <Text fontSize="sm" fontFamily="mono" color="gray.500">
                    {subscriptionData?.subscription_id || 'N/A'}
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="500" color="gray.700" mb={1}>
                    Customer Email
                  </Text>
                  <Text color="gray.900">{subscriptionData?.customer_email || 'N/A'}</Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="500" color="gray.700" mb={1}>
                    Currency
                  </Text>
                  <Text color="gray.900">{subscriptionData?.currency?.toUpperCase() || 'N/A'}</Text>
                </Box>
              </VStack>

              <Divider />

              <VStack spacing={3} align="stretch">
                <Text fontSize="sm" fontWeight="500" color="gray.700">
                  Quick Actions
                </Text>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDownloadInvoice}
                  leftIcon={<FiDownload />}
                >
                  Download Invoice
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleViewBillingHistory}
                  leftIcon={<FiCalendar />}
                >
                  View Billing History
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleContactSupport}
                  leftIcon={<FiMail />}
                >
                  Contact Support
                </Button>
                
                <Divider />
                
                <VStack spacing={1} align="start">
                  <Text fontSize="xs" color="gray.500" fontWeight="500">
                    Need Help?
                  </Text>
                  <Link 
                    href="mailto:support@weetrak.org" 
                    color="blue.500" 
                    fontSize="sm"
                    _hover={{ textDecoration: "underline" }}
                  >
                    support@weetrak.org
                  </Link>
                </VStack>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Change Plan Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Subscription Plan</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Select New Plan</FormLabel>
                <Select
                  placeholder="Choose a plan"
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                >
                  {plansData?.plans?.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - {formatCurrency(plan.price, plan.currency)}/{plan.interval}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <Text fontSize="sm" color="gray.600">
                Your plan will be updated immediately. Any prorated charges or credits will be applied to your next billing cycle.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdatePlan}
              isLoading={updatePlanIsLoading}
            >
              Update Plan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Cancel Subscription Dialog */}
      <AlertDialog
        isOpen={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        leastDestructiveRef={undefined}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Cancel Subscription
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack spacing={4} align="stretch">
                <Text>
                  Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period.
                </Text>
                <FormControl>
                  <FormLabel fontSize="sm">Reason for cancellation (optional)</FormLabel>
                  <Textarea
                    placeholder="Please let us know why you're canceling..."
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    rows={3}
                  />
                </FormControl>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={() => setIsCancelDialogOpen(false)}>
                Keep Subscription
              </Button>
              <Button
                colorScheme="red"
                onClick={handleCancelSubscription}
                ml={3}
                isLoading={cancelSubscriptionIsLoading}
              >
                Cancel Subscription
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Reactivate Subscription Dialog */}
      <AlertDialog
        isOpen={isReactivateDialogOpen}
        onClose={() => setIsReactivateDialogOpen(false)}
        leastDestructiveRef={undefined}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reactivate Subscription
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to reactivate your subscription? Your billing will resume immediately.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={() => setIsReactivateDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={handleReactivateSubscription}
                ml={3}
                isLoading={reactivateSubscriptionIsLoading}
              >
                Reactivate
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default SubscriptionSection; 